import React from "react";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import { StaticImage } from "gatsby-plugin-image";

import {
  SectionCol,
  SectionContainer,
  SectionInner,
} from "components/rhombus-UI/theme/containers";
import { FlexRow, FlexRowCenter, TextContainer } from "components/rui/layout";
import { Body, HeroTitle } from "components/rui/typography";
import RhombusButton from "components/common/RhombusButton";
import { FloatingAlert, FloatingNotification } from "./floatingElements";
import { CUSTOM_QUOTE_LINK } from "components/data/landing-page-data";

const HeroContainer = styled(SectionContainer)`
  position: relative;
  margin-top: -80px;
  padding-top: 160px;
  min-height: 700px;

  @media (max-width: 900px) {
    min-height: calc(100vh - 110px);
    padding-top: 80px;
    .secondary-btn {
      display: none;
    }
  }
`;

const HeroLeft = styled(SectionCol)`
  z-index: 2;

  @media (min-width: 1200px), (max-width: 1050px) {
    padding-right: 2.75rem;
  }

  @media (max-width: 1024px) {
    padding-right: 1.25rem;
  }
`;

const HeroRight = styled(SectionCol)`
  @media (max-width: 900px) {
    display: none;
  }
`;

const HeroImageContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  padding: 2.5rem 3.75rem;
  border-radius: 0 0 0 16px;
  overflow: hidden;
  height: 700px;
  width: 763px;

  @media (min-width: 2000px) {
    width: 900px;
  }

  @media (max-width: 1440px) {
    width: 660px;
  }

  @media (max-width: 1200px) {
    width: 550px;
  }

  @media (max-width: 1024px) {
    width: 440px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  background-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0) 50%,
    var(--primary-bg) 100%
  );
`;

const MobileImageOverlay = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    background-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      var(--primary-bg) 100%
    );
  }
`;

const FloatingElementsContainer = styled(FlexRow)`
  width: 100%;
  height: 45%;
  justify-content: center;
  align-items: flex-start;
  gap: 5rem;

  @media (max-width: 1200px) {
    gap: 2.5rem;
  }

  @media (max-width: 1024px) {
    gap: 0;
  }
`;

const MobileAlertWrapper = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
`;

const imageStyles = css`
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  height: 100% !important;
  width: 100% !important;
  border-radius: 0 0 0 16px !important;
`;

const mobileImageStyles = css`
  display: none !important;
  @media (max-width: 900px) {
    display: block !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    height: 100% !important;
    width: 100% !important;
    z-index: 0 !important;
  }
`;

const HomePageHero = () => {
  return (
    <HeroContainer color="var(--primary-bg)">
      <StaticImage
        src="./assets/home-hero.webp"
        alt="Built to protect. Designed to adapt."
        loading="eager"
        className={mobileImageStyles}
      />
      <MobileImageOverlay />
      <SectionInner>
        <HeroLeft>
          <TextContainer center>
            <HeroTitle>Built to protect. Designed to adapt.</HeroTitle>
            <Body large light>
              Experience the future of cloud-managed physical security that's
              open, secure, and fully interoperable. Safeguard spaces, protect
              people, and streamline operations on a single, integrated
              platform.
            </Body>
            <FlexRowCenter gap="28px">
              <RhombusButton title="See Demo" path="/live-demo/" />
              <RhombusButton
                title="Request Custom Quote"
                type="secondary-light"
                path={CUSTOM_QUOTE_LINK}
                className="secondary-btn"
              />
            </FlexRowCenter>
            <MobileAlertWrapper>
              <FloatingAlert />
            </MobileAlertWrapper>
          </TextContainer>
        </HeroLeft>
        <HeroRight>
          <HeroImageContainer>
            <StaticImage
              src="./assets/home-hero.webp"
              alt="Built to protect. Designed to adapt."
              loading="eager"
              className={imageStyles}
            />
            <ImageOverlay />
            <FloatingElementsContainer>
              <FloatingNotification />
              <FloatingAlert />
            </FloatingElementsContainer>
          </HeroImageContainer>
        </HeroRight>
      </SectionInner>
    </HeroContainer>
  );
};

export default HomePageHero;
